import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LasidoChoiceGroup, LasidoPreInscriptionConfig, LasidoPreinscription } from '@app/models/lasido';
import { BaseConfigService, BoardConfigService, FamilyService, PermissionService } from '@app/services';
import { LasidoService } from '@app/services/lasido.service';
import { Subject, Subscription, forkJoin, interval } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

export interface UsagerForFilter {
  id: number,
  nom: string,
  prenom: string
}

@Component({
  selector: 'app-lasido-preinscription',
  templateUrl: './lasido-preinscription.component.html',
  styleUrls: ['./lasido-preinscription.component.scss'],
  standalone: false
})
export class LasidoPreinscriptionComponent implements OnInit, OnDestroy {

  preinscriptions: LasidoPreinscription[];
  preinscriptionsFiltered: LasidoPreinscription[];
  config: LasidoPreInscriptionConfig;
  permToCreate: boolean;
  permToEdit: boolean;
  addBtnUrl: string;
  preinscriptionEnabled: boolean;
  checkPermPreinscription: Subscription;

  drawerOpened = false;
  anneesScolaires: { id: number, name: string }[];
  listUsagers: UsagerForFilter[];
  filterUsagers: UsagerForFilter[] = [];
  filter: { anneesScolaires: number[], usagers: UsagerForFilter[] } = { anneesScolaires: [], usagers: [] };

  onDestroy$ = new Subject<void>();

  private baseConfigService = inject(BaseConfigService)

  constructor(
    private lasidoPreinscriptionService: LasidoService,
    private familyService: FamilyService,
    private router: Router,
    public permService: PermissionService,
    private boardService: BoardConfigService
  ) { }

  ngOnInit(): void {
    this.permToEdit = this.permService.hasPermission('lasido_edit') && this.familyService.currentFamily.active;
    this.permToCreate = this.permService.hasPermission('lasido_create') && this.familyService.currentFamily.active;

    this.baseConfigService.getFirstConf$("board-user")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(conf => {
        const cards = conf.content.cards;
        const card = this.boardService.findCardByComponentName(cards, 'lasido-preinscriptions');
        this.addBtnUrl = card?.addBtnDemarcheId ? `new/${card.addBtnDemarcheId}` : 'new';
      })

    this.loadData().subscribe(_ => {
      this.anneesScolaires = this.config.listAnneesScolairesToShowDossier;
      this.listUsagers = this.setListUsager(this.preinscriptions);
      if (this.config?.listAnneesScolairesToShowDossier?.length) {
        this.preinscriptions = this.preinscriptions.filter((preinsc: LasidoPreinscription) => {
          return this.config.listAnneesScolairesToShowDossier.some(annee => annee.id === preinsc.anneeScolaire);
        });
      }
      this.preinscriptionsFiltered = [...this.preinscriptions];
    });

    this.checkPermPreinscription = interval(1000).subscribe(_ => {
      if (this.config) {
        this.preinscriptionEnabled = this.lasidoPreinscriptionService.checkPermPreinscription(this.config);
      }
    });
  }

  loadData() {
    return this.familyService.currentFamily$.pipe(
      filter(f => !!f),
      takeUntil(this.onDestroy$),
      tap(_ => { this.preinscriptions = null; }),
      switchMap(f => {
        const loaders = [];

        loaders.push(this.familyService.getFamilyPreInscriptions(f, 'Lasido').pipe(tap((data: LasidoPreinscription[]) => {
          this.preinscriptions = data;
          this.preinscriptions.forEach(preinsc => {
            this.addIsCoursRetenuProperty(preinsc.choiceGroups);
          })
        })));
        loaders.push(this.lasidoPreinscriptionService.getConfigPreinscriptionForUser().pipe(tap(config => this.config = config)));

        return forkJoin(loaders);
      })
    );
  }

  addIsCoursRetenuProperty(choiceGroups: LasidoChoiceGroup[]): void {
    choiceGroups.forEach((group) => {
      group.cours.sort((a, b) => a.ordre - b.ordre)
      group.cours.forEach((course) => {
        course.isCoursRetenu = group._TabCoursRetenu.some((coursRetenu) => coursRetenu.id === course.id);
      });
    });
  }

  onClickEdit(item: LasidoPreinscription) {
    this.router.navigate([`account/lasido-preinscriptions/${item.id}`]);
  }

  setListUsager(preinscriptions: LasidoPreinscription[]): UsagerForFilter[] {
    let listUsagersPreinscriptions = preinscriptions.map(preinsc => {
      let id = preinsc.idAdulte ? preinsc.idAdulte : preinsc.idEnfant;
      return { id, nom: preinsc.nom, prenom: preinsc.prenom };
    })

    return listUsagersPreinscriptions.filter((value, index, self) =>
      index === self.findIndex((usager) => (
        usager.id === value.id && usager.nom === value.nom && usager.prenom === value.prenom
      ))
    )
  }

  closeDrawer() {
    this.drawerOpened = false;
  }

  togglePreinscriptionsOptions() {
    this.drawerOpened = !this.drawerOpened;
  }

  changeSelectAnneeScolaire(data: number[]) {
    this.applyFilter();
  }

  changeSelectUsager(data: UsagerForFilter[]) {
    this.applyFilter();
  }

  applyFilter() {
    if (this.filter.anneesScolaires.length || this.filterUsagers.length) {
      this.preinscriptionsFiltered = this.preinscriptions.filter((preinsc: LasidoPreinscription) => {

        let resultAnneesScolaires;
        let resultUsagers;

        if (this.filter.anneesScolaires.length) {
          resultAnneesScolaires = this.filter.anneesScolaires.includes(preinsc.anneeScolaire);
        } else {
          resultAnneesScolaires = true;
        }

        if (this.filterUsagers.length) {
          resultUsagers = this.filterUsagers.some((usager: UsagerForFilter) => {
            let id = preinsc.idAdulte ? preinsc.idAdulte : preinsc.idEnfant;
            return usager.id === id && usager.nom === preinsc.nom && usager.prenom === preinsc.prenom;
          })
        } else {
          resultUsagers = true;
        }

        return resultAnneesScolaires && resultUsagers;

      })
    } else {
      this.preinscriptionsFiltered = this.preinscriptions;
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.checkPermPreinscription?.unsubscribe();
  }
}
